import React, { useEffect, useState } from 'react';
import { Clock, UserCheck, Copy, BadgeCheck } from 'lucide-react';
import { WhatsAppButton } from './WhatsAppButton';
import { PlanCheckout } from './PlanCheckout';

const left = (end) => Math.max(0, new Date(end).getTime() - Date.now());

const fmt = (ms) => {
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}min ${String(s % 60).padStart(2, '0')}s`;
};

// Countdown of the 7-day free trial + shared test reader credentials
export const TrialBanner = ({ trial, onBuy }) => {
  const [ms, setMs] = useState(() => left(trial.ends_at));
  useEffect(() => {
    const t = setInterval(() => setMs(left(trial.ends_at)), 1000);
    return () => clearInterval(t);
  }, [trial.ends_at]);
  const copy = (v) => navigator.clipboard?.writeText(v);
  return (
    <div data-testid="trial-banner" style={{ background: '#fffbeb', border: '1px solid #f6e05e', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', flexWrap: 'wrap', gap: '1rem 2rem', alignItems: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Clock className="w-6 h-6" style={{ color: '#b7791f' }} />
        <div>
          <p style={{ fontWeight: 700, color: '#744210' }}>Teste gratuito: todas as funcionalidades liberadas</p>
          <p style={{ fontSize: '0.85rem', color: '#975a16' }}>
            Faltam <strong data-testid="trial-countdown">{fmt(ms)}</strong>. Depois, o painel e o catálogo ficam suspensos até você contratar o plano anual.
          </p>
        </div>
      </div>
      <div data-testid="trial-reader-box" style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'white', borderRadius: 8, padding: '0.6rem 0.8rem', border: '1px solid #fefcbf' }}>
        <UserCheck className="w-5 h-5" style={{ color: '#2f855a' }} />
        <div style={{ fontSize: '0.85rem', color: '#2d3748' }}>
          <p style={{ fontWeight: 600 }}>Leitor de teste do seu catálogo</p>
          <p>E-mail: <strong data-testid="trial-reader-email">{trial.reader_email}</strong>
            <button type="button" onClick={() => copy(trial.reader_email)} data-testid="trial-reader-copy-email" title="Copiar e-mail" style={{ border: 'none', background: 'none', cursor: 'pointer', marginLeft: 4 }}><Copy className="w-3 h-3" /></button>
          </p>
          <p>Senha: <strong data-testid="trial-reader-password">{trial.reader_password}</strong></p>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button type="button" onClick={onBuy} data-testid="trial-buy-plan-btn"
          style={{ background: '#0d9488', color: 'white', border: 'none', borderRadius: 999, padding: '0.6rem 1.3rem', fontWeight: 700, cursor: 'pointer' }}>
          Contratar plano anual
        </button>
        <WhatsAppButton testId="trial-whatsapp-btn" compact />
      </div>
    </div>
  );
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

export const PlanStatus = ({ plan, onBuy }) => {
  if (plan.status !== 'active') return null;
  const soon = plan.days_left !== null && plan.days_left <= 30;
  return (
    <div data-testid="plan-status" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem 1.5rem', background: soon ? '#fffbeb' : '#f0fdfa',
      border: `1px solid ${soon ? '#f6e05e' : '#99f6e4'}`, borderRadius: 12, padding: '0.8rem 1.1rem', marginBottom: '1.5rem' }}>
      <BadgeCheck className="w-5 h-5" style={{ color: '#0d9488' }} />
      <p style={{ color: '#2d3748', fontSize: '0.9rem' }}>
        Plano Editora anual ativo até <strong data-testid="plan-ends-at">{fmtDate(plan.ends_at)}</strong>
        {soon && <> · vence em {plan.days_left} dias</>}
      </p>
      {soon && <button type="button" onClick={onBuy} data-testid="plan-renew-btn" style={{ background: '#0d9488', color: 'white', border: 'none', borderRadius: 999, padding: '0.45rem 1.1rem', fontWeight: 700, cursor: 'pointer' }}>Renovar plano</button>}
    </div>
  );
};

export const TrialExpiredScreen = ({ message, onLogout, token, onPaid }) => (
  <div data-testid="trial-expired-screen" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f7fafc', padding: '1.5rem' }}>
    <div style={{ maxWidth: 560, width: '100%', background: 'white', borderRadius: 16, padding: '2rem', boxShadow: '0 10px 30px rgba(0,0,0,0.08)' }}>
      <div style={{ textAlign: 'center' }}>
        <Clock className="w-10 h-10" style={{ color: '#c53030', margin: '0 auto 0.75rem' }} />
        <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2d3748', marginBottom: '0.5rem' }}>Conta suspensa</h1>
        <p data-testid="trial-expired-message" style={{ color: '#4a5568', marginBottom: '1.25rem' }}>{message}</p>
      </div>
      <div style={{ borderTop: '1px solid #edf2f7', paddingTop: '1rem', marginBottom: '1rem' }}>
        <p style={{ fontWeight: 600, color: '#2d3748', marginBottom: '0.5rem' }}>Contrate o plano anual para reativar agora</p>
        <PlanCheckout token={token} onDone={onPaid} />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
        <WhatsAppButton testId="expired-whatsapp-btn" compact />
        <button type="button" onClick={onLogout} data-testid="trial-expired-logout-btn" style={{ background: '#2d3748', color: 'white', border: 'none', borderRadius: 999, padding: '0.5rem 1.4rem', fontWeight: 600, cursor: 'pointer' }}>Sair</button>
      </div>
    </div>
  </div>
);

